import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions, LoadFailed } from '@config/components';
import { useConfigContext } from '@config/context';
import type { ConfigUser } from '@config/interfaces';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { SignIn, UserRow } from '../interfaces';
import { getTagMappings, ROLES_LIST_NAME, SIGN_IN_LIST_NAME, userColumns } from '../tableConfig';
import { signInTag } from '../utils';

const SIGN_INS: SignIn[] = ['sso', 'credentials'];

export const UsersList = (): ReactElement => {
  const { t } = useTranslation();
  const { getUsers } = useConfigContext();
  const [users, setUsers] = useState<ConfigUser[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    if (!getUsers) return;

    let cancelled = false;
    setIsLoading(true);
    setHasFailed(false);
    getUsers()
      .then(result => {
        if (!cancelled) setUsers(result);
      })
      .catch(() => {
        if (!cancelled) setHasFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getUsers]);

  const signInLabels = useMemo(
    () => Object.fromEntries(SIGN_INS.map(method => [method, t(`config.users.signIn.${method}`)])) as Record<SignIn, string>,
    [t],
  );

  const dataSource: UserRow[] = useMemo(
    () =>
      (users ?? [])
        .map(user => ({
          username: user.username,
          name: user.name ?? '',
          email: user.email ?? '',
          signIn: signInTag(user, signInLabels),
          roles: [...user.groups].sort((a, b) => a.localeCompare(b)),
          createdAt: user.createdAt,
        }))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [signInLabels, users],
  );

  // The roles the pool actually holds, so the filter offers exactly those and nothing nobody has.
  const roleList = useMemo(() => {
    const roles = [...new Set(dataSource.flatMap(row => row.roles))].sort((a, b) => a.localeCompare(b));

    return Object.fromEntries(roles.map(role => [role, role]));
  }, [dataSource]);

  const valueLists = useMemo(
    () => ({ [SIGN_IN_LIST_NAME]: signInLabels, [ROLES_LIST_NAME]: roleList }),
    [roleList, signInLabels],
  );

  if (hasFailed) return <LoadFailed />;

  return (
    <Table
      id="configUsers"
      rowKey="username"
      dataSource={dataSource}
      columns={userColumns}
      valueLists={valueLists}
      tagMappings={getTagMappings()}
      loading={isLoading}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.users')}
    >
      <ConfigTableActions />
    </Table>
  );
};
